import axios from "axios";

// IMPORTANT: remove trailing slash
// const BASE_URL = "http://localhost:8000/api"; // Update as needed
const BASE_URL = "https://api.revisionhub.rw/api";

axios.defaults.baseURL = BASE_URL;
axios.defaults.withCredentials = false;
axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.headers.common["Content-Type"] = "application/json";

// Helpers
function getToken() {
  return localStorage.getItem("auth_token");
}
function setToken(token) {
  localStorage.setItem("auth_token", token);
}
function clearToken() {
  localStorage.removeItem("auth_token");
}

// Request Interceptor (GLOBAL)
axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ---- Refresh queue to prevent multiple refresh calls ----
let isRefreshing = false;
let pendingQueue = [];

function processQueue(error, token = null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
}

// Response Interceptor (GLOBAL)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const skipRefreshPaths = ["/login", "/register", "/refresh-token"];
    if (skipRefreshPaths.some((p) => originalRequest?.url?.includes(p))) {
      clearToken();
      // Use router navigation ideally; but if not available here:
      window.location.href = "/login";
      return Promise.reject(error);
    }

    if (originalRequest?._retry) {
      return Promise.reject(error);
    }

    const oldToken = getToken();
    if (!oldToken) {
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // If refresh already happening, wait
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axios(originalRequest)); // retry using axios
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // IMPORTANT: use a clean client to avoid interceptor recursion
      const refreshClient = axios.create({
        baseURL: BASE_URL,
        withCredentials: false,
      });

      const refreshResponse = await refreshClient.post(
        "/refresh-token",
        {},
        { headers: { Authorization: `Bearer ${oldToken}` } },
      );

      const newToken = refreshResponse.data.token;
      if (!newToken) throw new Error("No token returned from refresh");

      setToken(newToken);
      processQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axios(originalRequest); // retry original
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearToken();
      window.location.href = "/login";
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default axios;
export { BASE_URL };
